import React, {useMemo, useRef, useState, useEffect} from 'react';
import {useLocation, useParams} from 'react-router-dom';
import {toast} from 'react-toastify';
import {connect} from 'react-redux';
import {
  DashboardLayout,
  PrimaryButton,
  SecondaryButton,
} from '../../../../components';
import * as Actions from '../../../../store/actions';
import {
  biodataIc,
  copyBlue,
  documentText,
  userTick,
  profiledRiskDark,
} from '../../../../assets/images/images';
import {Constants} from '../../../../helpers';
import ReusableLink from '../../../Verifications/ReusableLink';
import moment from 'moment';
import {useQuery} from 'react-query';
import {fetchEventDetails} from '../../../../requests/queries/flows';
import {getStatusTextAndStyles} from '../../../../helpers/getStatusTextAndStyles';
import {useCasesDetails} from '../hooks/useCasesDetails';
import AssessmentGaugeChart from '../../../EasyCreditCheck/AssessmentGaugeChart';
import EventReport from './EventReport';
import Breadcrumbs from 'components/BreadCrumbs';
import {PAGES_URL} from 'helpers/constants';
import {pdf} from '@react-pdf/renderer';
import {convertSVGToBase64} from 'helpers/convertSVGToBase64';
import {FtrDocument} from '../pdfs/FtrDocument';
import {CtrDocument} from '../pdfs/CtrDocument';

function Layout({children, auth: {user, userDetails}}) {
  const {slug, id} = useParams();
  const {state} = useLocation();
  const reportRef = useRef(null);
  const [downloadingType, setDownloadingType] = useState(null);
  const [base64URL, setBase64URL] = useState('');

  const flowName = state?.flowName || localStorage.getItem('flowName') || '';
  const {data: eventData, isLoading} = useQuery(['event_details', slug], () =>
    fetchEventDetails(slug),
  );

  useEffect(() => {
    convertSVGToBase64(profiledRiskDark).then(image => setBase64URL(image));
  }, []);

  const eventDetails = useMemo(
    () => !isLoading && eventData && eventData,
    [eventData, isLoading],
  );

  const isBankingTransaction =
    eventDetails?.transaction_data?.type === 'banking';

  const vStatus = eventDetails?.final_outcome?.toLowerCase() || 'pending';
  const riskScore = eventDetails?.evaluation_result?.risk_score || 0;
  const outcome = eventDetails?.evaluation_result?.outcome || '';

  const copyText = text => {
    navigator.clipboard.writeText(text);
    toast.success(`ID copied`);
  };

  const {TRANSACTION} = useCasesDetails({eventDetails});

  const projectId = useMemo(
    () => id || TRANSACTION?.project_id,
    [TRANSACTION?.project_id, id],
  );

  const handleDownload = async type => {
    if (downloadingType) return;

    setDownloadingType(type);

    try {
      const DocumentComponent = type === 'FTR' ? FtrDocument : CtrDocument;
      const fileName = `${type.toLowerCase()}-${slug}.pdf`;

      const blob = await pdf(
        <DocumentComponent
          data={eventDetails}
          logoImage={base64URL}
          userDetails={userDetails}
          userName={user ? `${user.first_name} ${user.last_name}` : 'System'}
        />,
      ).toBlob();

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();

      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success(`${type} downloaded successfully`);
    } catch (error) {
      console.error(`Error generating ${type} PDF:`, error);
      toast.error(`Could not generate ${type} report.`);
    }

    setDownloadingType(null);
  };

  const LINKS = [
    {
      path: Constants.PAGES_URL.FLOWS_EVENT_DETAILS.replace(
        ':id',
        projectId,
      ).replace(':slug', slug),
      src: biodataIc,
      text: 'Event Information',
      state: {flowName},
    },
    {
      path: Constants.PAGES_URL.FLOWS_EVENT_RESULT_EVALUATION.replace(
        ':id',
        projectId,
      ).replace(':slug', slug),
      src: documentText,
      text: 'Evaluation Result',
      state: {flowName},
    },
    {
      path: Constants.PAGES_URL.FLOWS_EVENT_BEHAVIOURAL_ANALYSIS.replace(
        ':id',
        projectId,
      ).replace(':slug', slug),
      src: userTick,
      text: 'Transaction Analysis',
      state: {flowName},
    },
  ];

  return (
    <DashboardLayout
      bg="bg-white xl:bg-white80"
      breadCrumbs={
        <p className="text-tiny font-normal text-deepBlue">Fraud Flows</p>
      }
    >
      <div style={{position: 'absolute', left: '-9999px', top: '-9999px'}}>
        <EventReport ref={reportRef} eventDetails={eventDetails} />
      </div>

      <div className="flex items-center min-h-[52px] py-2 flex-wrap gap-3 justify-between mr-[-24px] ml-[-32px] px-8 border border-transparent border-b-[#E4E3E6]">
        <Breadcrumbs
          items={[
            {label: 'Fraud Flows', path: PAGES_URL.FLOWS},
            {label: flowName, path: `/flows/events/${id}`},
            {label: 'Event Details'},
          ]}
        />

        {isBankingTransaction && (
          <div className="flex items-center flex-wrap gap-3">
            <SecondaryButton
              buttonText={
                downloadingType === 'FTR' ? 'Downloading...' : 'Download FTR'
              }
              onClick={() => handleDownload('FTR')}
              disabled={!!downloadingType || isLoading}
            />

            <PrimaryButton
              buttonText={
                downloadingType === 'CTR' ? 'Downloading...' : 'Download CTR'
              }
              onClick={() => handleDownload('CTR')}
              disabled={!!downloadingType || isLoading}
            />
          </div>
        )}
      </div>

      <div className="flex-wrap items-start min-h-full xl:flex xl:flex-nowrap basis-full">
        <div className="basis-[21.14%] xl:sticky xl:top-[35px]">
          <div className="bg-white flex p-4 flex-col gap-4 pt-16 sm:pt-6 xl:pt-[35px]">
            <h2 className="hidden text-base font-medium sm:block text-grey whitespace-nowrap">
              Event Details
            </h2>
            <div className="flex gap-2 xl:p-2 xl:flex-col">
              {LINKS?.map(({path, src, text, state}) => (
                <ReusableLink
                  key={path}
                  path={path}
                  src={src}
                  text={text}
                  state={state}
                />
              ))}
            </div>
          </div>
        </div>
        {children}
        <div className="pt-10 xl:basis-[31.83%] px-4 sticky bg-white xl:bg-white80">
          <div className="flex items-center justify-between">
            <div
              className={`flex uppercase pt-[5px] pb-[5px] px-2 text-xs font-medium ${
                getStatusTextAndStyles(vStatus)?.solid
              } rounded-full w-fit items-start mb-3 gap-[2px] capitalize`}
            >
              {isLoading ? '...' : vStatus}
            </div>
          </div>
          <div className="flex items-center justify-between p-4 mt-0 text-xs uppercase rounded bg-white80 xl:bg-white">
            <div className="flex flex-col gap-1 text-grey">
              <p className="font-medium uppercase">Event id</p>
              <p>{`${eventDetails?.transaction_id?.toUpperCase() || ''}`}</p>
            </div>
            <button
              onClick={() => copyText(eventDetails?.transaction_id)}
              className="flex items-center gap-2 px-2 pt-[9px] pb-[7px] border rounded border-brandBlue w-fit"
            >
              <img src={copyBlue} alt="" width={14} height={14} />
              <span className="text-xs font-medium text-brandBlue">Copy</span>
            </button>
          </div>

          <div className="mt-4">
            <p className="mb-3 text-xs font-medium uppercase text-grey">
              Risk Level
            </p>
            <AssessmentGaugeChart
              score={eventDetails?.evaluation_result?.risk_score || 0}
              min={0}
              max={100}
            />
          </div>

          <div className="flex flex-wrap items-stretch gap-4 mt-4 lg:flex-nowrap">
            <div className="w-full p-4 rounded bg-white80 xl:bg-white">
              <div className="flex flex-col gap-1">
                <p className="uppercase text-grey text-xs">risk score</p>
                <p className="text-success text-xl">{riskScore}</p>
              </div>
            </div>
            <div className="w-full p-4 rounded bg-white80 xl:bg-white">
              <p className="text-xs font-medium uppercase text-grey">OUTCOME</p>
              <div
                className={`${
                  getStatusTextAndStyles(outcome).solid
                } mt-2 flex uppercase pt-[5px] pb-[3px] px-2 text-xs font-medium rounded-full w-fit items-start gap-[2px]`}
              >
                <span>{outcome || '-'}</span>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-stretch gap-4 mt-4 lg:flex-nowrap">
            <div className="w-full p-4 rounded bg-white80 xl:bg-white">
              <p className="text-xs font-medium uppercase text-grey">
                DATE & TIME CREATED
              </p>
              <p className="mt-2 text-sm text-body font-medium">
                {moment(eventDetails?.createdAt).format('DD MMM YYYY h:mm') ||
                  '-'}
              </p>
            </div>
            <div className="w-full p-4 rounded bg-white80 xl:bg-white">
              <p className="text-xs font-medium uppercase text-grey">
                DECISION TYPE
              </p>
              <div className="mt-2 flex uppercase pt-[5px] pb-[3px] px-2 text-xs font-medium bg-secondaryBlue text-white rounded-full w-fit items-start gap-[2px]">
                <span>manual</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

export default connect(state => state, Actions)(Layout);
